// frontend/src/components/HobbliSocial.js

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaBinoculars,
  FaCheck,
  FaComments,
  FaEnvelopeOpen,
  FaLayerGroup,
  FaList,
  FaSearch,
  FaThLarge,
  FaTrashAlt,
  FaUserFriends,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";

import { supabase } from "../supabaseClient";
import UserAvatar from "./UserAvatar";
import "./HobbliSocial.css";

export default function HobbliSocial({ currentUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const collectionSectionRef = useRef(null);

  // ============================================================
  // MODALS
  // ============================================================

  const [showCollectModal, setShowCollectModal] = useState(false);

  // Open the incoming friend-request screen from a bell notification.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("view") !== "friend-requests") return;
    setShowCurrentModal(false);
    setShowCollectModal(true);
    params.delete("view");
    navigate(
      { pathname: location.pathname, search: params.toString() ? `?${params}` : "" },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);
  const [showCurrentModal, setShowCurrentModal] = useState(false);
  const [friendToRemove, setFriendToRemove] = useState(null);

  // ============================================================
  // FRIEND SEARCH
  // ============================================================

  // Kept as emailSearch because the existing Supabase RPC parameter
  // is still named email_query, even though the RPC now searches
  // both email and display_name.
  const [emailSearch, setEmailSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // ============================================================
  // FRIEND CONNECTIONS
  // ============================================================

  const [pendingRequests, setPendingRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [currentFriends, setCurrentFriends] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [uiNotice, setUiNotice] = useState(null);

  // ============================================================
  // FRIEND COLLECTION
  // ============================================================

  const [selectedFriend, setSelectedFriend] = useState(null);
  const [friendCategories, setFriendCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Keep the full selected category in memory so search works across
  // the entire category rather than only the visible page.
  const [allFriendItems, setAllFriendItems] = useState([]);
  const [friendItemsPage, setFriendItemsPage] = useState(1);
  const [friendItemsPerPage] = useState(20);

  const [collectionSearch, setCollectionSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewMode, setViewMode] = useState("grid");

  // ============================================================
  // HELPERS
  // ============================================================

  const getInitials = (name, fallback = "?") => {
    if (!name) return fallback;

    const parts = String(name)
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (parts.length === 0) return fallback;

    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }

    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`
      .toUpperCase();
  };

  const formatFriendDate = (dateValue) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const selectedFriendData = useMemo(
    () =>
      currentFriends.find(
        (friend) => String(friend.friend_id) === String(selectedFriend)
      ) || null,
    [currentFriends, selectedFriend]
  );

  // Existing friends are intentionally searched locally. The backend RPC
  // should continue excluding existing/pending connections so duplicate
  // friend requests cannot be sent.
  const existingFriendMatches = useMemo(() => {
    const query = emailSearch.trim().toLowerCase();

    if (!query || !hasSearched) return [];

    return currentFriends.filter((friend) => {
      const name = String(friend.friend_display_name || "").toLowerCase();
      const email = String(friend.friend_email || "").toLowerCase();

      return name.includes(query) || email.includes(query);
    });
  }, [currentFriends, emailSearch, hasSearched]);

  const filteredFriendItems = useMemo(() => {
    const query = collectionSearch.trim().toLowerCase();

    if (!query) return allFriendItems;

    return allFriendItems.filter((item) => {
      const searchableText = [
        item.name,
        item.year,
        item.brand,
        item.setName,
        item.condition,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [allFriendItems, collectionSearch]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredFriendItems.length / friendItemsPerPage)
  );

  const pagedFriendItems = useMemo(() => {
    const safePage = Math.min(Math.max(friendItemsPage, 1), totalPages);
    const start = (safePage - 1) * friendItemsPerPage;
    const end = start + friendItemsPerPage;

    return filteredFriendItems.slice(start, end);
  }, [
    filteredFriendItems,
    friendItemsPage,
    friendItemsPerPage,
    totalPages,
  ]);

  useEffect(() => {
    setFriendItemsPage(1);
  }, [collectionSearch]);

  useEffect(() => {
    if (friendItemsPage > totalPages) {
      setFriendItemsPage(totalPages);
    }
  }, [friendItemsPage, totalPages]);

  // ============================================================
  // FRIEND COLLECTION FETCHING
  // ============================================================

  const fetchFriendCategories = async (friendId) => {
    if (!friendId) return;

    try {
      const { data, error } = await supabase.rpc("get_friend_categories", {
        p_friend_id: friendId,
      });

      if (error) throw error;

      setFriendCategories(data || []);
      setSelectedCategory(null);
      setAllFriendItems([]);
      setCollectionSearch("");
      setFriendItemsPage(1);
    } catch (err) {
      console.error("Failed to fetch friend categories:", err);
      setUiNotice({ type: "error", message: "Failed to fetch this friend's categories." });
    }
  };

  const fetchFriendItems = async (friendId, categoryId) => {
    if (!friendId || !categoryId) return;

    try {
      const { data, error } = await supabase.rpc("get_friend_items", {
        p_friend_id: friendId,
        p_category_id: categoryId,
      });

      if (error) throw error;

      const mappedItems = (data || []).map((item) => ({
        id: item.id,
        name: item.name || "Unknown Item",
        brand: item.brand || "",
        setName: item.set_name || "",
        year: item.year || "",
        condition: item.condition || "",
        image_url: item.image_url || null,
      }));

      setAllFriendItems(mappedItems);
      setFriendItemsPage(1);
    } catch (err) {
      console.error("Failed to fetch friend items:", err);
      setUiNotice({ type: "error", message: "Failed to load this collection." });
    }
  };

  useEffect(() => {
    if (!selectedFriend || !selectedCategory) {
      return;
    }

    fetchFriendItems(selectedFriend, selectedCategory);
  }, [selectedFriend, selectedCategory]);

  // ============================================================
  // SEARCH USERS
  // ============================================================

  const handleFriendSearch = async () => {
    const query = emailSearch.trim();

    if (!query) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    setSearchLoading(true);
    setHasSearched(true);

    try {
      const { data, error } = await supabase.rpc("search_available_users", {
        email_query: query,
        p_current_user: currentUser.id,
      });

      if (error) throw error;

      setSearchResults(data || []);
    } catch (err) {
      console.error("Failed to search users:", err);
      setSearchResults([]);
      setUiNotice({ type: "error", message: "Failed to search for collectors." });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleFriendSearch();
    }
  };

  const clearFriendSearch = () => {
    setEmailSearch("");
    setSearchResults([]);
    setHasSearched(false);
  };

  // ============================================================
  // SEND FRIEND REQUEST
  // ============================================================

  const sendFriendRequest = async (recipientId) => {
    try {
      const { data, error } = await supabase.rpc(
        "send_connection_request",
        {
          target_user_id: recipientId,
          message: "",
        }
      );

      if (error) throw error;

      setSearchResults((prev) =>
        prev.filter((user) => user.id !== recipientId)
      );

      await fetchPendingRequests();

      if (data) {
        setUiNotice({ type: "success", message: String(data) });
      }
    } catch (err) {
      console.error("Failed to send friend request:", err);
      setUiNotice({ type: "error", message: "Failed to send friend request." });
    }
  };

  // ============================================================
  // FETCH PENDING REQUESTS
  // ============================================================

  const fetchPendingRequests = async () => {
    if (!currentUser?.id) return;

    try {
      const { data: connections, error: connectionsError } = await supabase
        .from("user_connections")
        .select("id, requester_id, receiver_id, created_at")
        .eq("status", "pending")
        .or(`requester_id.eq.${currentUser.id},receiver_id.eq.${currentUser.id}`)
        .order("created_at", { ascending: false });

      if (connectionsError) throw connectionsError;

      const requests = connections || [];
      const incoming = requests.filter((request) => request.receiver_id === currentUser.id);
      const outgoing = requests.filter((request) => request.requester_id === currentUser.id);
      const profileIds = [...incoming.map((r) => r.requester_id), ...outgoing.map((r) => r.receiver_id)].filter(Boolean);

      if (!profileIds.length) {
        setPendingRequests([]);
        setSentRequests([]);
        return;
      }

      const { data: profiles, error: profilesError } = await supabase.rpc(
        "get_user_profiles",
        { p_user_ids: [...new Set(profileIds)] }
      );
      if (profilesError) throw profilesError;

      const profilesById = new Map((profiles || []).map((profile) => [profile.id, profile]));

      setPendingRequests(incoming.map((request) => {
        const profile = profilesById.get(request.requester_id);
        return { ...request, requester_id: { id: request.requester_id, display_name: profile?.display_name || "Unknown User", profile_picture: profile?.profile_picture || null } };
      }));

      setSentRequests(outgoing.map((request) => {
        const profile = profilesById.get(request.receiver_id);
        return { ...request, recipient: { id: request.receiver_id, display_name: profile?.display_name || "Unknown User", profile_picture: profile?.profile_picture || null } };
      }));
    } catch (err) {
      console.error("Failed to fetch pending requests:", err);
    }
  };

  const handleCancelSentRequest = async (connectionId) => {
    try {
      const { error } = await supabase
        .from("user_connections")
        .delete()
        .eq("id", connectionId)
        .eq("requester_id", currentUser.id)
        .eq("status", "pending");
      if (error) throw error;
      setSentRequests((prev) => prev.filter((request) => request.id !== connectionId));
      if (emailSearch.trim()) await handleFriendSearch();
    } catch (err) {
      console.error("Failed to cancel friend request:", err);
      setUiNotice({ type: "error", message: "Failed to cancel friend request." });
    }
  };

  // ============================================================
  // FETCH CURRENT FRIENDS
  // ============================================================

  const fetchCurrentFriends = async () => {
    if (!currentUser?.id) return;

    try {
      const { data, error } = await supabase.rpc(
        "get_user_connections"
      );

      if (error) throw error;

      setCurrentFriends(data || []);
    } catch (err) {
      console.error("Failed to fetch current friends:", err);
    }
  };

  // ============================================================
  // ACCEPT / DECLINE REQUESTS
  // ============================================================

  const handleAcceptRequest = async (connectionId) => {
    try {
      const { data, error } = await supabase.rpc(
        "accept_connection",
        {
          request_id: connectionId,
        }
      );

      if (error) throw error;

      setPendingRequests((prev) =>
        prev.filter((request) => request.id !== connectionId)
      );

      await fetchCurrentFriends();

      if (data) {
        setUiNotice({ type: "success", message: String(data) });
      }
    } catch (err) {
      console.error("Failed to accept friend request:", err);
      setUiNotice({ type: "error", message: "Failed to accept friend request." });
    }
  };

  const handleDeclineRequest = async (connectionId) => {
    try {
      const { data, error } = await supabase.rpc(
        "deny_connection",
        {
          request_id: connectionId,
        }
      );

      if (error) throw error;

      setPendingRequests((prev) =>
        prev.filter((request) => request.id !== connectionId)
      );

      if (data) {
        setUiNotice({ type: "success", message: String(data) });
      }
    } catch (err) {
      console.error("Failed to decline friend request:", err);
      setUiNotice({ type: "error", message: "Failed to decline friend request." });
    }
  };

  // ============================================================
  // REMOVE FRIEND
  // ============================================================

  const handleRemoveFriend = async (friend) => {
    if (!friend?.connection_id) return;

    try {
      const { data, error } = await supabase.rpc(
        "remove_connection",
        {
          p_connection_id: friend.connection_id,
        }
      );

      if (error) throw error;

      setCurrentFriends((prev) =>
        prev.filter(
          (item) => item.connection_id !== friend.connection_id
        )
      );

      if (String(selectedFriend) === String(friend.friend_id)) {
        setSelectedFriend(null);
        setFriendCategories([]);
        setSelectedCategory(null);
        setAllFriendItems([]);
        setCollectionSearch("");
      }

      setFriendToRemove(null);

      if (data) {
        setUiNotice({ type: "success", message: String(data) });
      }
    } catch (err) {
      console.error("Failed to remove friend:", err);
      setUiNotice({ type: "error", message: "Failed to remove friend." });
    }
  };

  // ============================================================
  // INITIAL DATA + REALTIME
  // ============================================================

  useEffect(() => {
    if (!currentUser?.id) return undefined;

    fetchPendingRequests();
    fetchCurrentFriends();

    const requestsChannel = supabase
      .channel(`friend_requests_${currentUser.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "user_connections",
          filter: `receiver_id=eq.${currentUser.id}`,
        },
        () => {
          fetchPendingRequests();
        }
      )
      .subscribe();

    const sentRequestsChannel = supabase
      .channel(`sent_friend_requests_${currentUser.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_connections", filter: `requester_id=eq.${currentUser.id}` },
        () => fetchPendingRequests()
      )
      .subscribe();

    const notificationsChannel = supabase
      .channel(`social_notifications_${currentUser.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${currentUser.id}`,
        },
        (payload) => {
          setNotifications((prev) => [
            payload.new,
            ...prev,
          ]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(requestsChannel);
      supabase.removeChannel(sentRequestsChannel);
      supabase.removeChannel(notificationsChannel);
    };
  }, [currentUser?.id]);

  // ============================================================
  // COLLECTION NAVIGATION
  // ============================================================

  const openFriendCollection = async (friend) => {
    if (!friend?.friend_id) return;

    setShowCurrentModal(false);
    setSelectedFriend(friend.friend_id);
    setSelectedCategory(null);
    setAllFriendItems([]);
    setCollectionSearch("");

    await fetchFriendCategories(friend.friend_id);

    window.setTimeout(() => {
      collectionSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleFriendSelection = (friendId) => {
    const normalizedFriendId = friendId || null;

    setSelectedFriend(normalizedFriendId);
    setFriendCategories([]);
    setSelectedCategory(null);
    setAllFriendItems([]);
    setCollectionSearch("");
    setFriendItemsPage(1);

    if (normalizedFriendId) {
      fetchFriendCategories(normalizedFriendId);
    }
  };

  const handleCategorySelection = (categoryId) => {
    setSelectedCategory(categoryId || null);
    setAllFriendItems([]);
    setCollectionSearch("");
    setFriendItemsPage(1);
  };

  // ============================================================
  // MAIN SOCIAL CARDS
  // ============================================================

  const sections = [
    {
      title: "Find Collectors",
      description:
        "Search the Hobbli community and grow your collector network.",
      icon: FaBinoculars,
      actionText: "Find collectors",
      onClick: () => setShowCollectModal(true),
    },
    {
      title: "My Friends",
      description:
        "Manage your connections and explore what your friends collect.",
      icon: FaUsers,
      actionText: `${currentFriends.length} ${
        currentFriends.length === 1 ? "friend" : "friends"
      }`,
      onClick: () => setShowCurrentModal(true),
    },
    {
      title: "Messages",
      description:
        "Chat privately with your Hobbli friends and fellow collectors.",
      icon: FaEnvelopeOpen,
      actionText: "Open messages",
      onClick: () => navigate("/messages"),
    },
  ];

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="hobbli-social">
      <div className="hobbli-social__inner">
        {uiNotice ? (
        <div role="status" style={{ margin: "0 0 14px", padding: "10px 12px", borderRadius: "8px", background: uiNotice.type === "error" ? "rgba(220, 53, 69, 0.10)" : "rgba(47, 128, 237, 0.10)", color: uiNotice.type === "error" ? "#b42318" : "inherit", fontSize: ".88rem" }}>
          {uiNotice.message}
        </div>
      ) : null}
        {/* ====================================================
            HERO
            ==================================================== */}

        <section className="social-hero">
          <img
            src="/Logos/Collectors_Assemble.png"
            alt="Collectors Assemble"
            className="social-hero__logo"
          />

          <div className="social-hero__content">
            <span className="social-eyebrow">Hobbli Social</span>

            <h1>Collect together.</h1>

            <p>
              Find collectors, connect with friends, and
              discover collections across the Hobbli community.
            </p>
          </div>

          <button
            type="button"
            className="social-back-button"
            onClick={() => navigate("/collection")}
          >
            <FaArrowLeft />
            <span>Back to My Collection</span>
          </button>
        </section>

        {/* ====================================================
            SOCIAL ACTION CARDS
            ==================================================== */}

        <section className="social-actions">
          {sections.map((section) => {
            const Icon = section.icon;

            return (
              <button
                type="button"
                key={section.title}
                className="social-action-card"
                onClick={section.onClick}
              >
                <span className="social-action-card__icon">
                  <Icon />
                </span>

                <span className="social-action-card__body">
                  <span className="social-action-card__title">
                    {section.title}
                  </span>

                  <span className="social-action-card__description">
                    {section.description}
                  </span>
                </span>

                <span className="social-action-card__footer">
                  {section.actionText}
                  <span aria-hidden="true">→</span>
                </span>
              </button>
            );
          })}
        </section>

        {/* ====================================================
            EXPLORE FRIEND COLLECTIONS
            ==================================================== */}

        <section
          className="friend-collection-panel"
          ref={collectionSectionRef}
        >
          <div className="friend-collection-panel__header">
            <div>
              <span className="social-section-label">
                Community Collections
              </span>

              <h2>Explore Friend Collections</h2>

              <p>
                Pick a friend and category, then search their
                collection for a specific item.
              </p>
            </div>

            <div className="friend-collection-panel__header-icon">
              <FaLayerGroup />
            </div>
          </div>

          <div className="collection-toolbar">
            <div className="collection-toolbar__selectors">
              <label className="social-field">
                <span className="social-field__label">
                  Friend
                </span>

                <select
                  value={selectedFriend || ""}
                  onChange={(event) =>
                    handleFriendSelection(event.target.value)
                  }
                >
                  <option value="">Select a friend</option>

                  {currentFriends.map((friend) => (
                    <option
                      key={friend.connection_id}
                      value={friend.friend_id}
                    >
                      {friend.friend_display_name ||
                        friend.friend_email}
                    </option>
                  ))}
                </select>
              </label>

              <label className="social-field">
                <span className="social-field__label">
                  Category
                </span>

                <select
                  value={selectedCategory || ""}
                  disabled={!selectedFriend}
                  onChange={(event) =>
                    handleCategorySelection(event.target.value)
                  }
                >
                  <option value="">
                    {selectedFriend
                      ? "Select a category"
                      : "Choose a friend first"}
                  </option>

                  {friendCategories.map((category) => (
                    <option
                      key={category.id}
                      value={category.id}
                    >
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div
              className="view-toggle"
              aria-label="Collection view mode"
            >
              <button
                type="button"
                className={
                  viewMode === "grid"
                    ? "view-toggle__button is-active"
                    : "view-toggle__button"
                }
                onClick={() => setViewMode("grid")}
                title="Grid view"
              >
                <FaThLarge />
                <span>Grid</span>
              </button>

              <button
                type="button"
                className={
                  viewMode === "list"
                    ? "view-toggle__button is-active"
                    : "view-toggle__button"
                }
                onClick={() => setViewMode("list")}
                title="List view"
              >
                <FaList />
                <span>List</span>
              </button>
            </div>
          </div>

          {selectedFriend && selectedFriendData && (
            <div className="selected-collector-strip">
              <UserAvatar
                className="social-avatar social-avatar--small"
                src={selectedFriendData.friend_profile_picture}
                name={
                  selectedFriendData.friend_display_name ||
                  selectedFriendData.friend_email
                }
              />

              <div>
                <strong>
                  {selectedFriendData.friend_display_name ||
                    selectedFriendData.friend_email}
                </strong>

                <span>
                  {selectedCategory
                    ? `${allFriendItems.length} ${
                        allFriendItems.length === 1
                          ? "item"
                          : "items"
                      } in this category`
                    : "Choose a category to view their collection"}
                </span>
              </div>
            </div>
          )}

          {selectedFriend && selectedCategory && (
            <div className="collection-search-row">
              <div className="collection-search">
                <FaSearch />

                <input
                  type="text"
                  value={collectionSearch}
                  onChange={(event) =>
                    setCollectionSearch(event.target.value)
                  }
                  placeholder="Search this collection by item, year, brand, set..."
                />

                {collectionSearch && (
                  <button
                    type="button"
                    onClick={() => setCollectionSearch("")}
                    aria-label="Clear collection search"
                  >
                    ×
                  </button>
                )}
              </div>

              {collectionSearch && (
                <span className="collection-search-count">
                  {filteredFriendItems.length}{" "}
                  {filteredFriendItems.length === 1
                    ? "match"
                    : "matches"}
                </span>
              )}
            </div>
          )}

          {/* ----------------------------------------------------
              EMPTY STATES
              ---------------------------------------------------- */}

          {!selectedFriend && (
            <div className="social-empty-state">
              <div className="social-empty-state__icon">
                <FaUserFriends />
              </div>

              <h3>Choose a friend to start exploring</h3>

              <p>
                Public collection categories will appear here
                once you select someone from your network.
              </p>
            </div>
          )}

          {selectedFriend &&
            friendCategories.length === 0 && (
              <div className="social-empty-state">
                <div className="social-empty-state__icon">
                  <FaLayerGroup />
                </div>

                <h3>No collection categories yet</h3>

                <p>
                  This collector does not currently have any
                  viewable collection items.
                </p>
              </div>
            )}

          {selectedFriend &&
            friendCategories.length > 0 &&
            !selectedCategory && (
              <div className="social-empty-state">
                <div className="social-empty-state__icon">
                  <FaLayerGroup />
                </div>

                <h3>Select a category</h3>

                <p>
                  Choose one of this collector&apos;s categories
                  above to browse their items.
                </p>
              </div>
            )}

          {selectedFriend &&
            selectedCategory &&
            allFriendItems.length > 0 &&
            filteredFriendItems.length === 0 && (
              <div className="social-empty-state">
                <div className="social-empty-state__icon">
                  <FaSearch />
                </div>

                <h3>No matching items</h3>

                <p>
                  Try a different item name, year, brand, set, or
                  condition.
                </p>
              </div>
            )}

          {selectedFriend &&
            selectedCategory &&
            allFriendItems.length === 0 && (
              <div className="social-empty-state">
                <div className="social-empty-state__icon">
                  <FaLayerGroup />
                </div>

                <h3>No items to show</h3>

                <p>
                  There are no public items available in this
                  category.
                </p>
              </div>
            )}

          {/* ----------------------------------------------------
              GRID VIEW
              ---------------------------------------------------- */}

          {selectedFriend &&
            selectedCategory &&
            pagedFriendItems.length > 0 &&
            viewMode === "grid" && (
              <div className="friend-items-grid">
                {pagedFriendItems.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className="friend-collection-card"
                    onClick={() => setSelectedItem(item)}
                  >
                    <div className="friend-collection-card__image">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.name}
                        />
                      ) : (
                        <div className="friend-item-placeholder">
                          <FaLayerGroup />
                        </div>
                      )}
                    </div>

                    <div className="friend-collection-card__content">
                      <div className="friend-collection-card__title">
                        {item.year && (
                          <span>{item.year} </span>
                        )}

                        {item.brand && (
                          <span>{item.brand} </span>
                        )}

                        {item.name}
                      </div>

                      {item.setName && (
                        <div className="friend-collection-card__subtitle">
                          {item.setName}
                        </div>
                      )}

                      {item.condition && (
                        <span className="collection-meta-pill">
                          {item.condition}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

          {/* ----------------------------------------------------
              LIST VIEW
              ---------------------------------------------------- */}

          {selectedFriend &&
            selectedCategory &&
            pagedFriendItems.length > 0 &&
            viewMode === "list" && (
              <div className="friend-items-list">
                {pagedFriendItems.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className="friend-collection-row"
                    onClick={() => setSelectedItem(item)}
                  >
                    <div className="friend-collection-row__image">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.name}
                        />
                      ) : (
                        <FaLayerGroup />
                      )}
                    </div>

                    <div className="friend-collection-row__content">
                      <strong>
                        {item.year
                          ? `${item.year} • `
                          : ""}
                        {item.brand
                          ? `${item.brand} — `
                          : ""}
                        {item.name}
                      </strong>

                      <span>
                        {item.setName ||
                          "No set information"}
                      </span>
                    </div>

                    {item.condition && (
                      <span className="collection-meta-pill">
                        {item.condition}
                      </span>
                    )}

                    <span className="friend-collection-row__arrow">
                      →
                    </span>
                  </button>
                ))}
              </div>
            )}

          {/* ----------------------------------------------------
              PAGINATION
              ---------------------------------------------------- */}

          {filteredFriendItems.length > friendItemsPerPage && (
            <div className="collection-pagination">
              <button
                type="button"
                disabled={friendItemsPage <= 1}
                onClick={() =>
                  setFriendItemsPage((prev) =>
                    Math.max(1, prev - 1)
                  )
                }
              >
                Previous
              </button>

              <span>
                Page <strong>{friendItemsPage}</strong> of{" "}
                <strong>{totalPages}</strong>
              </span>

              <button
                type="button"
                disabled={friendItemsPage >= totalPages}
                onClick={() =>
                  setFriendItemsPage((prev) =>
                    Math.min(totalPages, prev + 1)
                  )
                }
              >
                Next
              </button>
            </div>
          )}
        </section>
      </div>

      {/* ========================================================
          FIND COLLECTORS MODAL
          ======================================================== */}

      {showCollectModal && (
        <div
          className="social-modal-overlay"
          onMouseDown={() => setShowCollectModal(false)}
        >
          <div
            className="social-modal social-modal--large"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="social-modal__close"
              onClick={() => setShowCollectModal(false)}
              aria-label="Close"
            >
              ×
            </button>

            <div className="social-modal__header">
              <div className="social-modal__header-icon">
                <FaUserPlus />
              </div>

              <div>
                <span className="social-section-label">
                  Build Your Network
                </span>

                <h2>Find Collectors</h2>

                <p>
                  Search by username or email. Existing friends
                  will appear too, so Hobbli works like a real
                  people search.
                </p>
              </div>
            </div>

            <div className="collector-search">
              <div className="collector-search__field">
                <FaSearch />

                <input
                  type="text"
                  value={emailSearch}
                  onChange={(event) => {
                    setEmailSearch(event.target.value);

                    if (!event.target.value.trim()) {
                      setSearchResults([]);
                      setHasSearched(false);
                    }
                  }}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search by username or email..."
                  autoFocus
                />

                {emailSearch && (
                  <button
                    type="button"
                    className="collector-search__clear"
                    onClick={clearFriendSearch}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>

              <button
                type="button"
                className="social-primary-button"
                onClick={handleFriendSearch}
                disabled={
                  searchLoading || !emailSearch.trim()
                }
              >
                <FaSearch />

                {searchLoading
                  ? "Searching..."
                  : "Search"}
              </button>
            </div>

            {/* --------------------------------------------------
                SEARCH RESULTS
                -------------------------------------------------- */}

            {hasSearched && (
              <section className="social-modal-section">
                <div className="social-modal-section__heading">
                  <div>
                    <h3>Search Results</h3>

                    <p>
                      Friends and collectors matching your search.
                    </p>
                  </div>

                  {!searchLoading && (
                    <span className="social-count-badge">
                      {existingFriendMatches.length +
                        searchResults.length}
                    </span>
                  )}
                </div>

                {searchLoading ? (
                  <div className="social-loading-state">
                    <span className="social-spinner" />
                    Searching Hobbli...
                  </div>
                ) : existingFriendMatches.length > 0 ||
                  searchResults.length > 0 ? (
                  <div className="collector-result-list">
                    {existingFriendMatches.map((friend) => {
                      const name =
                        friend.friend_display_name ||
                        friend.friend_email ||
                        "Hobbli Collector";

                      return (
                        <div
                          key={`friend-${friend.connection_id}`}
                          className="collector-result-card collector-result-card--friend"
                        >
                          <UserAvatar
                            className="social-avatar"
                            src={friend.friend_profile_picture}
                            name={name}
                          />

                          <div className="collector-result-card__identity">
                            <strong>{name}</strong>

                            {friend.friend_email &&
                              friend.friend_email !== name && (
                                <span>
                                  {friend.friend_email}
                                </span>
                              )}

                            <small className="already-friends-label">
                              <FaCheck />
                              Already friends
                            </small>
                          </div>

                          <div className="collector-result-card__actions">
                            <button
                              type="button"
                              className="social-secondary-button"
                              onClick={() =>
                                openFriendCollection(friend)
                              }
                            >
                              <FaLayerGroup />
                              View Collection
                            </button>

                            <button
                              type="button"
                              className="social-secondary-button"
                              onClick={() => navigate("/messages")}
                            >
                              <FaComments />
                              Message
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {searchResults.map((user) => {
                      const name =
                        user.display_name ||
                        user.email ||
                        "Hobbli Collector";

                      return (
                        <div
                          key={user.id}
                          className="collector-result-card"
                        >
                          <UserAvatar
                            className="social-avatar"
                            src={user.profile_picture}
                            name={name}
                          />

                          <div className="collector-result-card__identity">
                            <strong>{name}</strong>

                            {user.email &&
                              user.email !== name && (
                                <span>{user.email}</span>
                              )}
                          </div>

                          <button
                            type="button"
                            className="social-secondary-button"
                            onClick={() =>
                              sendFriendRequest(user.id)
                            }
                          >
                            <FaUserPlus />
                            Add Friend
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="social-empty-state social-empty-state--compact">
                    <div className="social-empty-state__icon">
                      <FaSearch />
                    </div>

                    <h3>No collectors found</h3>

                    <p>
                      Try another username or email address.
                    </p>
                  </div>
                )}
              </section>
            )}

            {/* --------------------------------------------------
                SENT REQUESTS
                -------------------------------------------------- */}

            <section className="social-modal-section">
              <div className="social-modal-section__heading">
                <div>
                  <h3>Sent Requests</h3>
                  <p>Friend requests you&apos;ve sent that are still waiting for a response.</p>
                </div>
                <span className="social-count-badge">{sentRequests.length}</span>
              </div>

              {sentRequests.length > 0 ? (
                <div className="friend-request-list">
                  {sentRequests.map((request) => {
                    const recipient = request.recipient || {};
                    const name = recipient.display_name || "Hobbli Collector";
                    return (
                      <div key={request.id} className="friend-request-card">
                        <UserAvatar className="social-avatar" src={recipient.profile_picture} name={name} />
                        <div className="friend-request-card__identity">
                          <strong>{name}</strong>
                          <span>Request pending</span>
                        </div>
                        <div className="friend-request-card__actions">
                          <button type="button" className="request-button request-button--decline" onClick={() => handleCancelSentRequest(request.id)}>
                            × Cancel Request
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="social-inline-empty">
                  <FaUserPlus />
                  <span>You don&apos;t have any sent requests waiting for a response.</span>
                </div>
              )}
            </section>

            {/* --------------------------------------------------
                PENDING REQUESTS
                -------------------------------------------------- */}

            <section className="social-modal-section">
              <div className="social-modal-section__heading">
                <div>
                  <h3>Friend Requests</h3>

                  <p>
                    Review collectors who want to connect with
                    you.
                  </p>
                </div>

                <span className="social-count-badge">
                  {pendingRequests.length}
                </span>
              </div>

              {pendingRequests.length > 0 ? (
                <div className="friend-request-list">
                  {pendingRequests.map((request) => {
                    const requester =
                      request.requester_id || {};

                    const name =
                      requester.display_name ||
                      requester.email ||
                      "Hobbli Collector";

                    return (
                      <div
                        key={request.id}
                        className="friend-request-card"
                      >
                        <UserAvatar
                          className="social-avatar"
                          src={requester.profile_picture}
                          name={name}
                        />

                        <div className="friend-request-card__identity">
                          <strong>{name}</strong>

                          {requester.email &&
                            requester.email !== name && (
                              <span>
                                {requester.email}
                              </span>
                            )}
                        </div>

                        <div className="friend-request-card__actions">
                          <button
                            type="button"
                            className="request-button request-button--accept"
                            onClick={() =>
                              handleAcceptRequest(request.id)
                            }
                          >
                            <FaCheck />
                            Accept
                          </button>

                          <button
                            type="button"
                            className="request-button request-button--decline"
                            onClick={() =>
                              handleDeclineRequest(
                                request.id
                              )
                            }
                          >
                            ×
                            Decline
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="social-inline-empty">
                  <FaUserFriends />

                  <span>
                    You don&apos;t have any pending friend
                    requests.
                  </span>
                </div>
              )}
            </section>

            {/* --------------------------------------------------
                RECENT SOCIAL NOTIFICATIONS
                -------------------------------------------------- */}

            {notifications.length > 0 && (
              <section className="social-modal-section">
                <div className="social-modal-section__heading">
                  <div>
                    <h3>Recent Activity</h3>
                    <p>New social activity from this session.</p>
                  </div>
                </div>

                <div className="social-notification-list">
                  {notifications.slice(0, 5).map((notification) => (
                    <div
                      className="social-notification"
                      key={notification.id}
                    >
                      <span className="social-notification__dot" />
                      <span>{notification.message}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      )}

      {/* ========================================================
          CURRENT FRIENDS MODAL
          ======================================================== */}

      {showCurrentModal && (
        <div
          className="social-modal-overlay"
          onMouseDown={() => setShowCurrentModal(false)}
        >
          <div
            className="social-modal social-modal--large"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="social-modal__close"
              onClick={() => setShowCurrentModal(false)}
              aria-label="Close"
            >
              ×
            </button>

            <div className="social-modal__header">
              <div className="social-modal__header-icon">
                <FaUserFriends />
              </div>

              <div>
                <span className="social-section-label">
                  Your Network
                </span>

                <h2>My Friends</h2>

                <p>
                  {currentFriends.length === 0
                    ? "Your collector network starts here."
                    : `${currentFriends.length} ${
                        currentFriends.length === 1
                          ? "collector"
                          : "collectors"
                      } in your network.`}
                </p>
              </div>
            </div>

            {currentFriends.length > 0 ? (
              <div className="friends-directory">
                {currentFriends.map((friend) => {
                  const name =
                    friend.friend_display_name ||
                    friend.friend_email ||
                    "Hobbli Collector";

                  return (
                    <div
                      key={friend.connection_id}
                      className="friend-directory-card"
                    >
                      <div className="friend-directory-card__profile">
                        <UserAvatar
                          className="social-avatar social-avatar--large"
                          src={friend.friend_profile_picture}
                          name={name}
                        />

                        <div className="friend-directory-card__identity">
                          <strong>{name}</strong>

                          {friend.friend_email &&
                            friend.friend_email !== name && (
                              <span>
                                {friend.friend_email}
                              </span>
                            )}

                          {friend.created_at && (
                            <small>
                              Friends since{" "}
                              {formatFriendDate(
                                friend.created_at
                              )}
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="friend-directory-card__actions">
                        <button
                          type="button"
                          className="social-primary-button social-primary-button--compact"
                          onClick={() =>
                            openFriendCollection(friend)
                          }
                        >
                          <FaLayerGroup />
                          View Collection
                        </button>

                        <button
                          type="button"
                          className="social-secondary-button"
                          onClick={() =>
                            navigate("/messages")
                          }
                        >
                          <FaComments />
                          Message
                        </button>

                        <button
                          type="button"
                          className="social-remove-button"
                          onClick={() =>
                            setFriendToRemove(friend)
                          }
                        >
                          <FaTrashAlt />
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="social-empty-state">
                <div className="social-empty-state__icon">
                  <FaUserFriends />
                </div>

                <h3>No friends yet</h3>

                <p>
                  Find other Hobbli collectors and start
                  building your network.
                </p>

                <button
                  type="button"
                  className="social-primary-button"
                  onClick={() => {
                    setShowCurrentModal(false);
                    setShowCollectModal(true);
                  }}
                >
                  <FaUserPlus />
                  Find Collectors
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================
          REACT REMOVE-FRIEND CONFIRMATION
          ======================================================== */}

      {friendToRemove && (
        <div
          className="social-confirm-overlay"
          onMouseDown={() => setFriendToRemove(null)}
        >
          <div
            className="social-confirm-dialog"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="social-confirm-dialog__icon">
              <FaTrashAlt />
            </div>

            <h3>Remove Friend?</h3>

            <p>
              Remove{" "}
              <strong>
                {friendToRemove.friend_display_name ||
                  friendToRemove.friend_email ||
                  "this collector"}
              </strong>{" "}
              from your Hobbli friends?
            </p>

            <span className="social-confirm-dialog__note">
              You can send them another friend request later.
            </span>

            <div className="social-confirm-actions">
              <button
                type="button"
                className="social-secondary-button"
                onClick={() => setFriendToRemove(null)}
              >
                Cancel
              </button>

              <button
                type="button"
                className="social-danger-button"
                onClick={() =>
                  handleRemoveFriend(friendToRemove)
                }
              >
                <FaTrashAlt />
                Remove Friend
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================
          COLLECTION ITEM MODAL
          ======================================================== */}

      {selectedItem && (
        <div
          className="social-modal-overlay"
          onMouseDown={() => setSelectedItem(null)}
        >
          <div
            className="social-modal social-modal--item"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="social-modal__close"
              onClick={() => setSelectedItem(null)}
              aria-label="Close"
            >
              ×
            </button>

            <div className="collection-item-modal">
              <div className="collection-item-modal__image">
                {selectedItem.image_url ? (
                  <img
                    src={selectedItem.image_url}
                    alt={selectedItem.name}
                  />
                ) : (
                  <div className="friend-item-placeholder friend-item-placeholder--large">
                    <FaLayerGroup />
                  </div>
                )}
              </div>

              <div className="collection-item-modal__content">
                <span className="social-section-label">
                  Collection Item
                </span>

                <h2>{selectedItem.name}</h2>

                <div className="collection-item-details">
                  <div>
                    <span>Year</span>
                    <strong>
                      {selectedItem.year || "Unknown"}
                    </strong>
                  </div>

                  <div>
                    <span>Brand</span>
                    <strong>
                      {selectedItem.brand || "N/A"}
                    </strong>
                  </div>

                  <div>
                    <span>Set</span>
                    <strong>
                      {selectedItem.setName || "N/A"}
                    </strong>
                  </div>

                  <div>
                    <span>Condition</span>
                    <strong>
                      {selectedItem.condition || "N/A"}
                    </strong>
                  </div>
                </div>

                <button
                  type="button"
                  className="social-secondary-button collection-item-modal__close-button"
                  onClick={() => setSelectedItem(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
